// src/pages/PackageDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import PackageDetail from '../components/PackageDetail';
import BookingModal from '../components/BookingModal';
import Footer from '../components/Footer';
import PackageLoader from '../components/PackageLoader';
import { api } from '../lib/apiClient';
import type { Package, ItineraryItem, Review } from '../types/package';
import * as Icons from 'lucide-react';
import Seo from '../components/Seo';

type RawItineraryItem = {
  time: string;
  title: string;
  description: string;
  icon: string;
};

type RawReview = {
  author: string;
  rating: number;
  comment: string;
};

const PackageDetailPage: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();

  const [pkg, setPkg] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!packageId) {
        setError('Package ID is missing');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Fetch package with itinerary + reviews from backend API
        const rawPkg = await api.get<Record<string, unknown>>(`/api/packages/${packageId}`);

        const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
          Car: Icons.Car,
          MapPin: Icons.MapPin,
          Camera: Icons.Camera,
          Utensils: Icons.Utensils,
          Clock: Icons.Clock,
          Star: Icons.Star,
          CheckCircle: Icons.CheckCircle,
          Info: Icons.Info,
          Calendar: Icons.Calendar,
          Shield: Icons.Shield,
          Users: Icons.Users,
        };

        const rawItinerary = (rawPkg.itinerary_items ?? []) as RawItineraryItem[];
        const rawReviews = (rawPkg.reviewsList ?? []) as RawReview[];

        const itinerary: ItineraryItem[] = rawItinerary.map(item => ({
          time: item.time,
          title: item.title,
          description: item.description,
          icon: iconMap[item.icon] || Icons.Info,
        }));

        const reviewsList: Review[] = rawReviews.map(r => ({
          author: r.author,
          rating: r.rating,
          comment: r.comment,
        }));

        const formatted: Package = {
          id: rawPkg.id as number,
          name: rawPkg.name as string,
          category: rawPkg.category as string,
          duration: (rawPkg.duration ?? '') as string,
          groupSize: (rawPkg.group_size ?? '') as string,
          price: (rawPkg.price ?? '') as string,
          rating: (rawPkg.rating ?? 0) as number,
          reviews: (rawPkg.reviews ?? reviewsList.length) as number,
          image: (rawPkg.image_url ?? '') as string,
          gallery: (rawPkg.gallery ?? []) as string[],
          description: (rawPkg.description ?? '') as string,
          highlights: (rawPkg.highlights ?? []) as string[],
          itinerary,
          included: (rawPkg.included ?? []) as string[],
          notIncluded: (rawPkg.not_included ?? []) as string[],
          requirements: (rawPkg.requirements ?? []) as string[],
          reviewsList,
          image_url: undefined
        };

        setPkg(formatted);
      } catch (e) {
        console.error(e);
        setError('Failed to load package details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [packageId]);

  const handleBack = () => navigate('/packages');
  const handleBookNow = () => setIsBookingOpen(true);

  const handleConfirm = async (data: {
    name: string;
    email: string;
    date: string;
    participants: number;
    notes: string;
  }) => {
    if (!pkg) return;
    try {
      await api.post('/api/bookings', {
        package_id: pkg.id,
        name: data.name,
        email: data.email,
        date: data.date,
        participants: data.participants,
        notes: data.notes,
      });

      // Close modal and show success
      setIsBookingOpen(false);
      setShowSuccess(true);

      // Hide success after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);

    } catch (e) {
      console.error(e);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-red-400">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <>
    {(() => {
      // Fallbacks keep tags stable while data loads
      const title = pkg?.name ? `${pkg.name} — Jordan Tour | Above & Below Adventures` : 'Jordan Tour Package | Above & Below Adventures';
  const description = pkg?.description || 'Full itinerary, inclusions, and booking for this Jordan tour package.';
  const image = (pkg?.image || pkg?.gallery?.[0] || '/assets/jordan.png');
  const CURRENCY = import.meta.env.VITE_CURRENCY || 'USD';

  const tripLd = {
    '@type': 'TouristTrip',
    name: pkg?.name || 'Jordan Tour Package',
    description,
    image,
    url: `${import.meta.env.VITE_SITE_URL}/packages/${packageId}`,
    ...(pkg?.rating ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: pkg.rating,
        reviewCount: pkg.reviews || (pkg.reviewsList?.length ?? 0)
      }
    } : {}),
    ...(pkg?.price ? {
      offers: {
        '@type': 'Offer',
        price: String(pkg.price),
        priceCurrency: CURRENCY,
        availability: 'http://schema.org/InStock',
        url: `${import.meta.env.VITE_SITE_URL}/packages/${packageId}`
      }
    } : {})
  };

  return (
    <Seo
      title={title}
      description={description}
      canonicalPath={`/packages/${packageId}`}
      image={image}
      jsonLd={tripLd}
    />
  );
})()}

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-orange-900 text-white">
        <Header />
        <div className="min-h-screen pb-12 relative">
          <div className="container mx-auto px-4 py-6">
            {loading || !pkg ? (
              <PackageLoader detailMode />
            ) : (
              <PackageDetail
                packageItem={pkg}
                onBookNow={handleBookNow}
                onBack={handleBack}
              />
            )}
          </div>

          {/* Success message */}
          {showSuccess && (
            <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
              Booking confirmed! 🎉
            </div>
          )}
        </div>
        <Footer />
      </div>
      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        onConfirm={handleConfirm}
        packageItem={pkg}
      />
    </>
  );
};

export default PackageDetailPage;
